import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Alert, CircularProgress, Snackbar, Tabs, Tab } from '@mui/material';
import {
  getTeamHealth, syncGoals, generateNotes, setGoalOutcome,
} from '../../../services/sprintHealthService';
import { isAuthenticated } from '../../../services/authService';
import { A } from './reviewUi';
import DetailsTab from './ReviewDetails';
import { SummaryHeader, SummaryTiles } from './ReviewSummary';
import { TeamHealthSection } from './ReviewDetails';
import NotesPanel from './NotesPanel';


// ── View ─────────────────────────────────────────────────────────────────────

/**
 * Sprint Review → Team view. Summary tab: the verdict, team health and every number as a tile, with the notes.
 * Details tab: the full section behind each number. `toggle` is the Team/Leadership switch; a new
 * `refreshKey` (after the header's Refresh pulled the sprint from ADO) reloads the data.
 */
const TeamHealthView = ({ sprint, team, toggle, refreshKey = 0 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [toast, setToast] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  // The tab is part of the URL (?tab=details) so the leadership email can link straight to the details.
  const tab = searchParams.get('tab') === 'details' ? 'details' : 'summary';
  const setTab = useCallback((v) => setSearchParams((prev) => {
    const n = new URLSearchParams(prev);
    if (v === 'details') n.set('tab', 'details'); else n.delete('tab');
    return n;
  }, { replace: true }), [setSearchParams]);
  const [focus, setFocus] = useState(null);
  const [effortOpen, setEffortOpen] = useState(false);
  const loggedIn = isAuthenticated();

  const load = useCallback(async (refresh = false) => {
    if (!sprint || !team) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getTeamHealth(sprint, team, refresh);
      setData(res.data);
    } catch (err) {
      setData(null);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sprint, team]);

  useEffect(() => { load(); }, [load]);
  // The header's Refresh already recomputed the metrics; read them again.
  const seenRefresh = useRef(refreshKey);
  useEffect(() => {
    if (seenRefresh.current === refreshKey) return;
    seenRefresh.current = refreshKey;
    load();
  }, [refreshKey, load]);
  // Back to Summary only when the sprint or team really changes. setTab can't be a dependency:
  // setSearchParams gets a new identity on every URL change, so opening Details would bounce back.
  const shown = useRef(`${sprint}|${team}`);
  const setTabRef = useRef(setTab);
  setTabRef.current = setTab;
  useEffect(() => {
    const key = `${sprint}|${team}`;
    if (shown.current === key) return;
    shown.current = key;
    setTabRef.current('summary');
    setFocus(null);
    setEffortOpen(false);
  }, [sprint, team]);

  // After a tile or jump click: scroll the section into view and outline it for a moment.
  useEffect(() => {
    if (tab !== 'details' || !focus) return undefined;
    document.getElementById(`sr-${focus}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const t = setTimeout(() => setFocus(null), 1400);
    return () => clearTimeout(t);
  }, [tab, focus]);

  const select = (id, openUnlinked = false) => {
    setTab('details');
    setFocus(id);
    if (openUnlinked) setEffortOpen(true);
  };

  const run = async (key, fn, done) => {
    setBusy(key);
    setError(null);
    try {
      const res = await fn();
      if (done) setToast(typeof done === 'function' ? done(res) : done);
      await load(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };

  const statusRow = (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, alignItems: 'center', pb: 2 }}>{toggle}</Box>
  );

  if (loading && !data) return <>{statusRow}<Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box></>;
  if (!data) return <>{statusRow}{error && <Alert severity="info">{error}</Alert>}</>;

  const c = data.current;
  const hist = data.history;
  const delivery = c.delivery || { moved: c.commitments.filter((m) => m.itemsClosed > 0).length, total: c.commitments.length };
  const notesOpen = data.notesEditable !== false;
  const editable = loggedIn && notesOpen;
  const goalEditable = loggedIn && !c.closed;
  const onOutcome = (o) => run('outcome', () => setGoalOutcome(sprint, team, o), o ? 'Goal outcome saved' : 'Goal outcome reset to ADO');
  const onSync = () => run('goal', () => syncGoals(sprint, team), (r) => (r.data?.[0]?.error ? `Goal sync: ${r.data[0].error}` : 'Sprint goal synced from ADO'));

  return (
    <Box>
      {statusRow}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        aria-label="Sprint review sections"
        sx={{ mb: 2.5, borderBottom: `1px solid ${A.line}`, '& .MuiTabs-indicator': { bgcolor: '#D74B00' }, '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '0.9375rem' } }}
      >
        <Tab value="summary" label="Summary" />
        <Tab value="details" label="Details" />
      </Tabs>

      {tab === 'summary' ? (
        <Box>
          {/* Layout B: the verdict, then team health (sprint retro beside practice and retro actions,
              then Start / Stop / Continue), then the numbers and notes. */}
          <Box sx={{ mb: 2.5 }}>
            <SummaryHeader
              data={data}
              delivery={delivery}
              editable={goalEditable}
              onSelect={(id) => select(id, id === 'effort')}
              onSync={onSync}
            />
          </Box>
          <Box sx={{ mb: 2.5 }}>
            <TeamHealthSection c={c} hist={hist} data={data} team={team} sprint={sprint} editable={goalEditable} loggedIn={loggedIn} onChanged={() => load(true)} />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0,3fr) minmax(0,2fr)' }, gap: 2.5, alignItems: 'stretch' }}>
            <SummaryTiles c={c} hist={hist} delivery={delivery} onSelect={(id) => select(id)} />
            <NotesPanel
              fill
              notes={data.notes}
              editable={editable}
              sprint={sprint}
              team={team}
              onChange={(notes) => setData((d) => ({ ...d, notes }))}
              onDraft={() => run('notes', () => generateNotes(sprint, team), 'AI notes drafted')}
              drafting={busy === 'notes'}
            />
          </Box>
        </Box>
      ) : (
        <DetailsTab
          key={refreshKey}
          c={c}
          hist={hist}
          data={data}
          delivery={delivery}
          team={team}
          sprint={sprint}
          editable={goalEditable}
          loggedIn={loggedIn}
          busy={busy}
          focus={focus}
          effortOpen={effortOpen}
          onOutcome={onOutcome}
          onSync={onSync}
          onChanged={() => load(true)}
          onJump={(id) => select(id)}
        />
      )}

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')} message={toast} />
    </Box>
  );
};

export default TeamHealthView;
